import React from 'react';

import moment from 'moment';

import { connect, useSelector } from 'react-redux';
import t from 'react-translate';

// schemas
import { RootState } from 'redux/schemas';
import { NTimelineExercise } from 'redux/schemas/models/exercise';
import { ActivityType } from 'redux/schemas/models/activity';
import { ComponentKeyPluralized } from 'redux/schemas/models/lecture-component';

// actions
import { setActivityExpandedInAutomatedCommunications } from 'redux/actions/course-communications';

// selectors
import { getExercise, hasExerciseGotCommunicationError } from 'redux/selectors/timeline';
import { isActivityExpandedInAutomatedCommunications } from 'redux/selectors/course-communications';

// components
import NvIcon from 'shared/components/nv-icon';
import { NvTooltip } from 'shared/components/nv-tooltip';
import NvStackedTab from 'shared/components/nv-stacked-tab';

import CommunicationCreateNewDropdown from 'course_communications/components/communication-create-new-dropdown';
import CommunicationItem from 'course_communications/components/communication-item/communication-item';

type ExerciseLectureComponentProps = {
  exerciseId: number
};

type StateProps = {
  exercise: NTimelineExercise,
  hasCommunicationError: boolean,
};

const mapDispatchToProps = {
  setExpanded: setActivityExpandedInAutomatedCommunications,
};

const ExerciseLectureComponent = (props: ExerciseLectureComponentProps & StateProps & typeof mapDispatchToProps) => {
  const isExpanded = useSelector<RootState, boolean>((state) => isActivityExpandedInAutomatedCommunications(state, ComponentKeyPluralized.EXERCISE, props.exerciseId));

  return (
    <React.Fragment>
      <div className={`lecture-component-row ${isExpanded && props.exercise?.communicationsCount > 0 && 'expanded-box'}`}>
        <NvIcon icon='assignments' size='smallest' />
        <div className='description'>{props.exercise?.title}</div>
        {props.exercise?.hasStructuralIssues && (
          <div className='text-danger spacing'>
            <NvIcon icon='warning' size='xss-smallest' />
          </div>
        )}
        {props.exercise?.hardDeadline && (
          <NvTooltip text={t.TIMELINE.HARD_DEADLINE()} placement='top'>
            <div className='spacing'>
              <NvIcon icon='harddeadline' size='smallest' />
            </div>
          </NvTooltip>
        )}
        <div className='date'>
          {props.exercise?.deadline && t.LECTURE_PAGES.COMPONENTS.DUE_DATE.STUDENT_VIEW(moment(props.exercise.deadline).format('LLL'))}
        </div>
        <div className=''>
          <CommunicationCreateNewDropdown activityType={ActivityType.EXERCISE} activityId={props.exerciseId} />
        </div>
        { props.exercise?.communicationsCount > 0 && (
          <NvStackedTab
            count={props.exercise.communicationsCount}
            hasError={props.hasCommunicationError}
            isExpanded={isExpanded}
            errorMessage={t.COURSE_COMMUNICATIONS.WARNINGS.ACTIVITY_COMMUNICATION()}
            onClick={() => props.setExpanded({
              type: ComponentKeyPluralized.EXERCISE,
              id: props.exerciseId,
              isExpanded: !isExpanded,
            })}
          />
        )}
      </div>
      {
        // Communications
        isExpanded && props.exercise?.communications && props.exercise.communications.map(communicationId => (
          <CommunicationItem key={communicationId.toString()} communicationId={communicationId} />
        ))
      }
    </React.Fragment>
  );
};

const ConnectedExerciseLectureComponent = connect(
  (state: RootState, ownProps: ExerciseLectureComponentProps): StateProps => ({
    exercise: getExercise(state, ownProps.exerciseId),
    hasCommunicationError: hasExerciseGotCommunicationError(state, ownProps.exerciseId),
  }),
  mapDispatchToProps,
)(ExerciseLectureComponent);

export default ConnectedExerciseLectureComponent;
